import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import TextInput from '../../components/ui/TextInput.jsx';
import Button from '../../components/ui/Button';
import DecisionRadio from './DecisionRadio';

// Task 27 — "Revizyon Talepleri" sekmesi karar modalı. GET v1/period-goals/
// revisions(/team) satırı zaten Changes (alan → {old,new}) + GoalTitle +
// EmployeeName ile ZENGİNLEŞTİRİLMİŞ geliyor (bkz. backend
// PeriodGoalRevisionResponse) — ayrı bir detay ucuna ihtiyaç yok. Onay zinciri
// PeriodGoal'ün kendi 7 durumundan BAĞIMSIZ: manager_approval→hr_approval→applied
// (veya herhangi bir aşamada →rejected). Decision burada approve|reject (goal
// kararındaki approve|revision'dan FARKLI bir kelime dağarcığı — backend
// ApproveRevisionAsync). Not BE'de zorunlu DEĞİL (yalnız goal-level manager/
// hr-decision'ın revision seçeneği not ister) — yine de kullanıcı red gerekçesi
// girebilsin diye alan burada da sunulur, opsiyonel.
const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1400;
  padding: 24px;
  overflow-y: auto;
`;

const ModalCard = styled.div`
  background: #fff;
  border-radius: 8px;
  max-width: 640px;
  width: 100%;
  padding: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  max-height: 90vh;
  overflow-y: auto;
  margin: auto;
`;

const DialogTitle = styled.h3`
  margin: 0 0 16px;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FieldGroup = styled.div`
  margin-bottom: 16px;
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 20px 0 10px;
  border-bottom: 1px solid #E8ECF0;
  padding-bottom: 6px;
`;

const ReadonlyValue = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  padding: 4px 0;
`;

const ChangeRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
  background: #F5F7FA;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 6px;
  font-size: 12px;
`;

const ChangeField = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const OldValue = styled.div`
  color: #B02A37;
  text-decoration: line-through;
`;

const NewValue = styled.div`
  color: #1A6E38;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

// "kr:<guid>" alan anahtarları kullanıcıya okunaklı gösterilmez — kısa etiket.
const changeFieldLabel = (t, field) => {
  if (field === 'Title') return t('performance.goal.fields.title');
  if (field === 'Description') return t('performance.goal.fields.description');
  if (field === 'KpiTargetValue') return t('performance.goal.kpiTargetValue');
  // Plan 3, Task 6/7 — hedef ağırlığı revizyonu (bkz. GoalEditor handleRequestRevision).
  if (field === 'WeightPct') return t('performance.goal.fields.weight');
  if (field === 'KpiDirection') return t('performance.goal.kpiDirectionLabel');
  if (field.startsWith('kr:')) return t('performance.goal.keyResultTitle');
  // P3 (2026-10-08) — yeni beyaz liste anahtarları (son tarih, kilometre taşı adımı, eşik koşulu).
  if (field === 'DueDate') return t('performance.goal.fields.dueDate');
  if (field.startsWith('stepWeight:')) return t('performance.goal.milestone.stepWeight');
  if (field.startsWith('stepDue:')) return t('performance.goal.milestone.stepDueDate');
  if (field.startsWith('step:')) return t('performance.goal.milestone.step');
  if (field === 'ThresholdOperator') return t('performance.goal.threshold.operatorLabel');
  if (field === 'ThresholdValue') return t('performance.goal.threshold.value');
  return field;
};

// Müşteri geri bildirimi (2026-08-10): değerler birimsiz gösterilince onaylayan
// "%20 mi, 20 adet mi" ayıramıyordu. Hedef/baz değerlere hedefin kendi birimi
// (GoalDecisionModal'daki "değer + birim etiketi" deseninin aynısı), cap/ağırlık
// alanlarına her zaman % eklenir. Birim yalnız DOLU değerlere eklenir ('-' hariç).
const changeValueSuffix = (t, field, kpiUnit) => {
  if (field === 'KpiTargetValue' || field === 'KpiBaselineValue') {
    return kpiUnit ? ` ${t(`performance.goal.kpiUnit.${kpiUnit}`, { defaultValue: kpiUnit })}` : '';
  }
  if (field === 'KpiCapPct' || field === 'WeightPct' || field.startsWith('stepWeight:')) return ' %';
  if (field === 'ThresholdValue') {
    return kpiUnit ? ` ${t(`performance.goal.kpiUnit.${kpiUnit}`, { defaultValue: kpiUnit })}` : '';
  }
  return '';
};

// Hedef yönü (2026-08-10): ham "increase"/"decrease" değil, çeviri etiketi gösterilir.
const changeValueDisplay = (t, field, value, kpiUnit) => {
  if (value == null || value === '') return '-';
  if (field === 'KpiDirection') return t(`performance.goal.kpiDirection.${value}`, { defaultValue: value });
  if (field === 'ThresholdOperator') return t(`performance.goal.threshold.operator.${value}`, { defaultValue: value });
  return `${value}${changeValueSuffix(t, field, kpiUnit)}`;
};

export default function RevisionDecisionModal({ open, revision, isHr, onClose, onDecided, decideRevisionManager, decideRevisionHr }) {
  const { t } = useTranslation();
  const [decision, setDecision] = useState('approve');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setDecision('approve'); setNote(''); }
  }, [open, revision]);

  const handleSubmit = useCallback(async () => {
    if (!revision) return;
    setBusy(true);
    const decide = isHr ? decideRevisionHr : decideRevisionManager;
    const ok = await decide(revision.id, decision, note.trim() || undefined);
    setBusy(false);
    if (ok) onDecided();
  }, [revision, isHr, decideRevisionHr, decideRevisionManager, decision, note, onDecided]);

  if (!open || !revision) return null;

  const changeEntries = Object.entries(revision.changes || {});

  return (
    <Modal>
      <ModalCard>
        <DialogTitle>{t('performance.teamGoals.decision.title')}</DialogTitle>

        <ReadonlyValue><strong>{revision.employeeName}</strong> — {revision.goalTitle}</ReadonlyValue>

        <SectionTitle>{t('performance.teamGoals.revisionChanges')}</SectionTitle>
        {changeEntries.length === 0 && <ReadonlyValue>-</ReadonlyValue>}
        {changeEntries.map(([field, change]) => (
          <ChangeRow key={field}>
            <ChangeField>{changeFieldLabel(t, field)}</ChangeField>
            <OldValue>{changeValueDisplay(t, field, change.old, revision.kpiUnit)}</OldValue>
            <NewValue>{changeValueDisplay(t, field, change.new, revision.kpiUnit)}</NewValue>
          </ChangeRow>
        ))}

        <SectionTitle>{t('performance.teamGoals.decision.title')}</SectionTitle>
        <FieldGroup>
          <DecisionRadio
            name="revision-decision"
            value={decision}
            onChange={setDecision}
            options={[
              { value: 'approve', label: t('performance.teamGoals.decision.approve') },
              { value: 'reject', label: t('performance.teamGoals.decision.reject') },
            ]}
          />
        </FieldGroup>
        <FieldGroup>
          <TextInput
            label={t('performance.teamGoals.decision.note')}
            type="textarea"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </FieldGroup>

        <Actions>
          <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} disabled={busy} loading={busy}>
            {t('performance.teamGoals.decision.submit')}
          </Button>
        </Actions>
      </ModalCard>
    </Modal>
  );
}
